import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/AllMarketplaces.graphql';
import { Query, QueryallMarketplacesArgs } from '../../__generated__/globalTypes';

export const useAllMarketplacesQuery = (options?: QueryHookOptions<Query, QueryallMarketplacesArgs>) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryallMarketplacesArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useAllMarketplacesLazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryallMarketplacesArgs>
): QueryResult<Query, QueryallMarketplacesArgs> & {
  getAllMarketplaces: LazyQueryExecFunction<Query, QueryallMarketplacesArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getAllMarketplaces, result] = useLazyQuery<Query, QueryallMarketplacesArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getAllMarketplaces, ...result };
};

export const useAllMarketplacesPromiseQuery = () => {
  const client = useApolloClient();

  const getAllMarketplaces = useCallback(
    (options?: Omit<QueryOptions<QueryallMarketplacesArgs, Query>, 'query'>) =>
      client.query<Query, QueryallMarketplacesArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getAllMarketplaces };
};
// Suspense Query Hooks
export const useAllMarketplacesSuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QueryallMarketplacesArgs>
) => {
  const result = useSuspenseQuery<Query, QueryallMarketplacesArgs>(QUERY, {
    ...options
  });

  return result;
};
